import React, { useRef } from "react";
import styled from "styled-components";
import { MainLayout, InnerLayout } from "../styles/Layouts";
import SectionTitle from "../Components/SectionTitle";
import ContactItem from "../Components/ContactItem";
import { Mail, Send } from "lucide-react";
import emailjs from "@emailjs/browser";
import { PageTransition, FadeIn } from "../Components/AnimatedComponents";

const ContactPage: React.FC = () => {
  const formRef = useRef<HTMLFormElement>(null);

  const sendEmail = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formRef.current) return;

    emailjs
      .sendForm(
        "service_zbbk4l5",
        "template_drp4en6",
        formRef.current,
        "user_h63R1mb17C2T6UBgZFwUg",
      )
      .then(
        () => {
          alert("Successfully sent email! Thank you for contacting me.");
          formRef.current?.reset();
        },
        () => {
          alert("Something went wrong. Please try again.");
        },
      );
  };

  return (
    <PageTransition>
      <MainLayout>
        <FadeIn direction="up">
          <SectionTitle title={"Contact"} span={"contact"} />
        </FadeIn>
        <ContactPageStyled>
          <InnerLayout className="contact-grid">
            <FadeIn direction="left" delay={0.1}>
              <div className="form-section">
                <h4>Get In Touch</h4>
                <p className="form-subtitle">
                  Have a question or want to work together? Drop me a message!
                </p>
                <form ref={formRef} onSubmit={sendEmail}>
                  <div className="form-row">
                    <div className="form-field">
                      <label htmlFor="name">Name</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        placeholder="Your name"
                        required
                      />
                    </div>
                    <div className="form-field">
                      <label htmlFor="email">Email</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        placeholder="your@email.com"
                        required
                      />
                    </div>
                  </div>
                  <div className="form-field">
                    <label htmlFor="subject">Subject</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      placeholder="What's this about?"
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="textarea">Message</label>
                    <textarea
                      id="textarea"
                      name="textarea"
                      rows={6}
                      placeholder="Your message..."
                      required
                    />
                  </div>
                  <button type="submit" className="submit-btn">
                    <Send size={16} />
                    Send Message
                  </button>
                </form>
              </div>
            </FadeIn>
            <FadeIn direction="right" delay={0.2}>
              <div className="info-section">
                <ContactItem
                  title={"Email"}
                  icon={<Mail size={20} />}
                  cont1={"saifaldindeveloper@gmail.com"}
                  cont2={"aldi1570@mylaurier.ca"}
                />
              </div>
            </FadeIn>
          </InnerLayout>
        </ContactPageStyled>
      </MainLayout>
    </PageTransition>
  );
};

const ContactPageStyled = styled.section`
  .contact-grid {
    display: grid;
    grid-template-columns: 1.5fr 1fr;
    gap: 3rem;
    align-items: start;

    @media screen and (max-width: 900px) {
      grid-template-columns: 1fr;
    }
  }

  .form-section {
    h4 {
      font-size: 1.5rem;
      font-weight: 700;
      margin-bottom: 0.5rem;
    }

    .form-subtitle {
      color: var(--font-light-color);
      font-size: 0.9rem;
      margin-bottom: 2rem;
    }

    .form-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1rem;

      @media screen and (max-width: 500px) {
        grid-template-columns: 1fr;
      }
    }

    .form-field {
      margin-bottom: 1.25rem;

      label {
        display: block;
        font-size: 0.8rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--white-color);
        margin-bottom: 0.5rem;
      }

      input,
      textarea {
        width: 100%;
        padding: 0.75rem 1rem;
        background: var(--card-bg);
        border: 1px solid var(--border-color);
        border-radius: 10px;
        color: var(--white-color);
        font-family: inherit;
        font-size: 0.9rem;
        transition: all 0.2s ease;
        outline: none;

        &::placeholder {
          color: var(--font-light-color);
          opacity: 0.5;
        }

        &:focus {
          border-color: var(--primary-color);
          box-shadow: 0 0 0 3px var(--background-light-color-2);
        }
      }

      textarea {
        resize: vertical;
        min-height: 120px;
      }
    }

    .submit-btn {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 2rem;
      background: var(--primary-color);
      color: white;
      border: none;
      border-radius: 10px;
      font-family: inherit;
      font-size: 0.9rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.25s ease;

      &:hover {
        background: var(--primary-color-dark);
        transform: translateY(-2px);
        box-shadow: 0 4px 15px rgba(99, 102, 241, 0.35);
      }

      &:active {
        transform: translateY(0);
      }
    }
  }

  .info-section {
    @media screen and (max-width: 900px) {
      order: -1;
    }
  }
`;

export default ContactPage;
